import { forwardRef } from 'react';
import { Link } from '@tanstack/react-router';
import type { LinkAsProps } from '@leanin/ui';

// `LinkAsProps` from packages/ui declares `href: string` — the contract was
// authored when next/link was the routing library. TanStack Router's `Link`
// takes `to` (pathname only) plus optional `search` (typed query) and
// `hash`. When the `href` carries `?query=…` or `#hash`, we MUST split
// them: passing the full string as `to` makes the router treat the literal
// `…?…`/`…#…` as the pathname (and re-append the current page's search
// state), and leaving a `#hash` inside the query string percent-encodes it
// into the last param's value (`?c=x%23message-y`). Splitting at the first
// `#` then `?` routes each part through its own prop; the destination
// route's `validateSearch` parses the params.
export const RouterLinkAs = forwardRef<HTMLAnchorElement, LinkAsProps>(
  function RouterLinkAs({ href, ...rest }, ref) {
    const h = href.indexOf('#');
    const hash = h < 0 ? undefined : href.slice(h + 1);
    const base = h < 0 ? href : href.slice(0, h);
    const q = base.indexOf('?');
    if (q < 0) {
      return (
        <Link
          ref={ref}
          to={base}
          {...(hash === undefined ? {} : { hash })}
          {...rest}
        />
      );
    }
    const to = base.slice(0, q);
    const params = new URLSearchParams(base.slice(q + 1));
    const search: Record<string, string> = {};
    for (const [k, v] of params) search[k] = v;
    // TanStack's `search` is typed against the destination route's
    // schema; this adapter is generic (one `href: string` slot for any
    // route), so we cast through `unknown` to silence the structural
    // mismatch. The destination route's `validateSearch` still runs at
    // navigation time and is the real type boundary.
    return (
      <Link
        ref={ref}
        to={to}
        search={search as unknown as never}
        {...(hash === undefined ? {} : { hash })}
        {...rest}
      />
    );
  },
);
